import { Link } from "react-router-dom";
import { ChevronRight, Home } from "lucide-react";

export interface DashboardBreadcrumbItem {
  key: string;
  label: string;
  href?: string;
}

const SEGMENT_LABELS: Record<string, string> = {
  admins: "Admins",
  announcement: "Announcement",
  "add-ifrs-quiz": "Add IFRS Quiz",
  "add-lecture": "Add Lecture",
  "add-mockup": "Add Mockup",
  "add-offline-lecture": "Add Offline Lecture",
  "add-quiz": "Add Quiz",
  "add-topic": "Add Topic",
  "assign-course": "Assign Course",
  blocked: "Blocked Students",
  blogs: "Blogs",
  books: "Books",
  "books-requests": "Books Requests",
  categories: "Categories",
  "cbq-exams": "CBQ Exams",
  consultants: "Consultants",
  coupons: "Coupons",
  courses: "Courses",
  currency: "Currency",
  "delete-mockups": "Delete Mockups",
  "delete-quiz": "Delete Quiz",
  "delete-topic": "Delete Topic",
  "edit-blog": "Edit Blog",
  "edit-book": "Edit Book",
  "edit-certificate": "Edit Certificate",
  "edit-consultant": "Edit Consultant",
  "edit-course": "Edit Course",
  "edit-course-content": "Course Content",
  "edit-offline-course": "Edit Offline Course",
  "exam-maker": "Exam Maker",
  exhibits: "Exhibits",
  faqs: "FAQs",
  feedback: "Feedback",
  "freeze-student": "Freeze Student",
  "host-quizs": "Hosted Quizzes",
  "ifrs-exam": "IFRS Exam",
  instructors: "Instructors",
  mails: "Mails",
  packages: "Packages",
  "purchase-requests": "Purchase Requests",
  "question-bank": "Question Bank",
  questions: "Questions",
  requests: "Requests",
  reservations: "Reservations",
  "response-options": "Response Options",
  reviews: "Reviews",
  roadmaps: "Roadmaps",
  sales: "Sales",
  scores: "Scores",
  "skip-quiz": "Skip Quiz",
  "student-attemps": "Student Attempts",
  "student-progress": "Student Progress",
  students: "Students",
  support: "Support",
  team: "Team",
  training: "Training",
  transactions: "Transactions",
  profile: "Profile",
};

const ID_SEGMENT_PATTERN =
  /^(\d+|[0-9a-f]{12,}|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;

const safeDecode = (segment: string) => {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
};

const normalizeSegment = (segment: string) => segment.toLowerCase();

const humanizeSegment = (segment: string) =>
  segment
    .replace(/[-_]+/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const getPreviousVisibleSegment = (visibleSegments: string[]) =>
  visibleSegments.length ? visibleSegments[visibleSegments.length - 1] : "";

const getDashboardRoot = (pathname: string) =>
  pathname.startsWith("/sales/dashboard") ? "/sales/dashboard" : "/dashboard";

const getDashboardSegments = (pathname: string) => {
  const segments = pathname.split("/").filter(Boolean);

  if (segments[0] === "sales" && segments[1] === "dashboard") {
    return segments.slice(2);
  }

  if (segments[0] === "dashboard") {
    return segments.slice(1);
  }

  return segments;
};

const isKnownRouteSegment = (segment: string) =>
  Boolean(SEGMENT_LABELS[normalizeSegment(segment)]) ||
  segment === "create" ||
  segment === "edit";

const shouldSkipSegment = (segment: string, visibleSegments: string[]) => {
  const normalizedSegment = normalizeSegment(segment);
  const previousSegment = getPreviousVisibleSegment(visibleSegments);

  if (isKnownRouteSegment(normalizedSegment)) {
    return false;
  }

  return (
    ID_SEGMENT_PATTERN.test(normalizedSegment) ||
    Boolean(previousSegment && isKnownRouteSegment(previousSegment))
  );
};

const getContextualActionLabel = (
  segment: string,
  visibleSegments: string[]
) => {
  const previousSegment = getPreviousVisibleSegment(visibleSegments);

  if (segment === "create") {
    if (previousSegment === "admins") return "Create Admin";
    if (previousSegment === "host-quizs") return "Create Hosted Quiz";
    if (previousSegment === "ifrs-exam") return "Create IFRS Exam";
    if (previousSegment === "questions") return "Create Question";
    if (previousSegment === "exhibits") return "Create Exhibit";
    if (previousSegment === "response-options") return "Create Response Option";
    return "Create";
  }

  if (segment === "edit") {
    if (previousSegment === "ifrs-exam") return "Edit IFRS Exam";
    if (previousSegment === "questions") return "Edit Question";
    if (previousSegment === "exhibits") return "Edit Exhibit";
    return "Edit";
  }

  return undefined;
};

const getSegmentLabel = (segment: string, visibleSegments: string[]) => {
  const normalizedSegment = normalizeSegment(segment);
  const contextualLabel = getContextualActionLabel(
    normalizedSegment,
    visibleSegments
  );

  return (
    contextualLabel ||
    SEGMENT_LABELS[normalizedSegment] ||
    humanizeSegment(segment)
  );
};

const getDynamicDetailLabel = (
  previousSegment: string,
  visibleSegments: string[]
) => {
  if (previousSegment === "transactions") return "Transaction Details";
  if (previousSegment === "cbq-exams") return "Edit CBQ Exam";
  if (
    previousSegment === "students" &&
    visibleSegments.includes("ifrs-exam")
  ) {
    return "Attempt Answers";
  }
  if (previousSegment === "students") return "Student Profile";

  return undefined;
};

export const buildDashboardBreadcrumbs = (
  pathname: string
): DashboardBreadcrumbItem[] => {
  const dashboardRoot = getDashboardRoot(pathname);
  const dashboardHome = `${dashboardRoot}/students`;
  const dashboardSegments = getDashboardSegments(pathname);
  const visibleSegments: string[] = [];
  const breadcrumbs: DashboardBreadcrumbItem[] = [
    {
      key: dashboardHome,
      label: "Dashboard",
      href: dashboardHome,
    },
  ];
  let currentPath = dashboardRoot;
  let skippedLastSegmentAfter = "";

  dashboardSegments.forEach((rawSegment) => {
    const decodedSegment = safeDecode(rawSegment);
    const normalizedSegment = normalizeSegment(decodedSegment);

    currentPath += `/${rawSegment}`;

    if (shouldSkipSegment(decodedSegment, visibleSegments)) {
      skippedLastSegmentAfter = getPreviousVisibleSegment(visibleSegments);
      return;
    }

    skippedLastSegmentAfter = "";
    visibleSegments.push(normalizedSegment);
    breadcrumbs.push({
      key: currentPath,
      label: getSegmentLabel(decodedSegment, visibleSegments.slice(0, -1)),
      href: currentPath,
    });
  });

  if (skippedLastSegmentAfter) {
    const detailLabel = getDynamicDetailLabel(
      skippedLastSegmentAfter,
      visibleSegments
    );

    if (detailLabel) {
      breadcrumbs.push({
        key: `${currentPath}-${detailLabel}`,
        label: detailLabel,
        href: currentPath,
      });
    }
  }

  if (breadcrumbs.length === 1 && pathname !== dashboardHome) {
    breadcrumbs[0] = { ...breadcrumbs[0], href: undefined };
    return breadcrumbs;
  }

  return breadcrumbs.map((item, index) =>
    index === breadcrumbs.length - 1 ? { ...item, href: undefined } : item
  );
};

export const getDashboardBackHref = (
  breadcrumbs: DashboardBreadcrumbItem[]
) => {
  if (breadcrumbs.length <= 2) return undefined;

  return breadcrumbs[breadcrumbs.length - 2].href;
};

export const getDashboardPageTitle = (
  breadcrumbs: DashboardBreadcrumbItem[]
) => breadcrumbs[breadcrumbs.length - 1]?.label || "Dashboard";

const DashboardBreadcrumbs = ({
  items,
}: {
  items: DashboardBreadcrumbItem[];
}) => {
  if (!items.length) return null;

  return (
    <nav aria-label="Dashboard breadcrumb" className="mt-1 max-w-full">
      <ol className="flex max-w-full items-center gap-1 overflow-x-auto whitespace-nowrap text-[11px] font-semibold text-lightGray [-ms-overflow-style:none] [scrollbar-width:none] sm:text-xs [&::-webkit-scrollbar]:hidden">
        {items.map((item, index) => {
          const isCurrent = index === items.length - 1;

          return (
            <li
              key={item.key}
              className="flex min-w-0 shrink-0 items-center gap-1"
            >
              {index > 0 && (
                <ChevronRight
                  size={13}
                  className="shrink-0 text-lightGray/50"
                />
              )}
              {item.href && !isCurrent ? (
                <Link
                  to={item.href}
                  className="inline-flex min-w-0 items-center gap-1 rounded-full px-1.5 py-1 text-lightGray transition hover:bg-secondary/10 hover:text-secondary"
                >
                  {index === 0 && <Home size={13} className="shrink-0" />}
                  <span className="max-w-[120px] truncate sm:max-w-[180px] lg:max-w-none">
                    {item.label}
                  </span>
                </Link>
              ) : (
                <span className="inline-flex min-w-0 items-center gap-1 rounded-full px-1.5 py-1 text-primary">
                  {index === 0 && <Home size={13} className="shrink-0" />}
                  <span className="max-w-[140px] truncate sm:max-w-[220px] lg:max-w-none">
                    {item.label}
                  </span>
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

export default DashboardBreadcrumbs;
